import { DOCUMENT, inject, Service } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EventManager } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { MngHotkeysHelpDialogComponent } from './mng-hotkeys-help-dialog.component';

export interface Hotkey {
  element: any;
  keys: string;
  description: string;
  callback: () => any;
}

export interface HotkeyInfo {
  description: string;
  subscription: Subscription;
}

@Service()
export class MngHotkeysService {
  private eventManager = inject(EventManager);
  private document = inject<Document>(DOCUMENT);
  private dialog = inject(MatDialog);

  defaults: Partial<Hotkey>;

  hotkeys: Map<string, HotkeyInfo> = new Map<string, HotkeyInfo>();

  helpOpen = false;

  dialogRef: MatDialogRef<MngHotkeysHelpDialogComponent>;

  _pause = false;

  constructor() {
    this.defaults = {
      element: this.document
    };

    this.register({
      keys: 'alt+h',
      description: 'Tastenkürzel-Hilfe',
      callback: () => {
        this.toggleHelpDialog();
      }
    });

    // Hotkeys werden bei offenen Dialogen deaktiviert
    this.dialog.afterOpened.subscribe((dialogRef) => {
      if (!(dialogRef.componentInstance instanceof MngHotkeysHelpDialogComponent)) {
        this.pause();
        if (this.helpOpen) {
          this.toggleHelpDialog();
        }
      }
    });
    this.dialog.afterAllClosed.subscribe(() => {
      this.resume();
    });
  }

  register(options: Partial<Hotkey>): string {
    const merged = { ...this.defaults, ...options, keys: options.keys.replace(/\+/, '.') };

    if (this.hotkeys.get(merged.keys)) {
      console.warn(`Hotkey ${options.keys} ist bereits registriert!`);
      return;
    }
    const event = `keydown.${merged.keys}`;

    const observable = new Observable((observer) => {
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this._pause) {
          observer.next(e);
        }
        return false;
      };

      const dispose = this.eventManager.addEventListener(merged.element, event, handler);

      return () => {
        dispose();
        this.hotkeys.delete(merged.keys);
      };
    });

    const sub = observable.subscribe(options.callback);
    this.hotkeys.set(merged.keys, { description: merged.description, subscription: sub });

    return merged.keys;
  }

  unregister(hotkeyKeys: string) {
    const realKeys = hotkeyKeys.replace(/\+/, '.');
    const hk = this.hotkeys.get(realKeys);

    if (hk) {
      hk.subscription.unsubscribe();
    } else {
      console.warn(`Hotkey ${hotkeyKeys} ist nicht registriert`);
    }
  }

  pause() {
    this._pause = true;
  }

  resume() {
    this._pause = false;
  }

  toggleHelpDialog() {
    if (!this.helpOpen) {
      this.helpOpen = true;
      const dialogRef = this.dialog.open(MngHotkeysHelpDialogComponent, {
        width: '360px',
        hasBackdrop: false,
        position: { bottom: '24px', right: '96px' },
        data: { registeredHotkeys: this.hotkeys }
      });
      dialogRef.afterClosed().subscribe(() => {
        this.helpOpen = false;
      });
      this.dialogRef = dialogRef;
    } else {
      this.helpOpen = false;
      this.dialogRef.close();
    }
  }

  registerAll(hotkeys: Partial<Hotkey>[]) {
    for (const hotkey of hotkeys) {
      this.register(hotkey);
    }
  }

  unregisterAll(hotkeys: Partial<Hotkey>[]) {
    for (const hotkey of hotkeys) {
      this.unregister(hotkey.keys);
    }
  }
}
